<template>
  <div>
    <exo-table :root="_self">
      <exo-form :root="_self">
        <exo-input-name
          :root="_self"
          gt_ParamOrbitTransfert1-v-step="5"
          alignWithParametricField
        />
        <exo-input-parametric
          :root="_self"
          name="platformMass"
          gt_ParamOrbitTransfert1-v-step="6"
          tooltip="Platform mass of the spacecraft, not including propulsion system and propellant"
        />
        <v-divider></v-divider>
        <exo-input-checkbox
          :disabled="inputs.attitudeDynamicsModelDefined"
          :root="_self"
          gt_ParamOrbitTransfert1-v-step="7"
          name="dragModelDefined"
        />
        <exo-input-parametric
          v-if="inputs.dragModelDefined"
          :root="_self"
          name="dragArea"
          tooltip="Used to consider the drag perturbation"
        >
          <exo-help-dialog title="Drag area">
            The (cross section) drag area of the spacecraft is the surface
            effectively contributing to the generation of drag. More precisely,
            this is given by the projection of the surface along the velocity
            vector (relative to the atmosphere).
            <br />
            <br />
            When considering the drag perturbation, the spacecraft is assumed to
            be a spherical body and thus the drag area in this case is given by
            the area of a great circle. In particular, it is independent of the
            attitude.
          </exo-help-dialog>
        </exo-input-parametric>
        <exo-input-parametric
          v-if="inputs.dragModelDefined"
          :root="_self"
          name="dragCoefficient"
          tooltip="Used to consider the drag perturbation"
        >
          <exo-help-dialog title="Drag coefficient">
            The drag coefficient <katex-element expression="C_d" /> of the
            spacecraft represents the spacecraft's susceptibility to drag
            forces.<br />
            A typical value for a spacecraft is 2.2 or 2.3.
          </exo-help-dialog>
        </exo-input-parametric>
        <exo-input-checkbox
          :root="_self"
          name="attitudeDynamicsModelDefined"
          :onChange="updateDragModelDefined"
        />
        <div v-if="inputs.attitudeDynamicsModelDefined" :root="_self">
          <exo-card-form label="Inertial centre in spacecraft frame">
            <template v-slot:titleTooltip>
              <exo-help-dialog
                margin="ml-5"
                title="Inertial centre in spacecraft frame"
              >
                <p>
                  Distance from the origin of spacecraft frame to the inertial
                  centre. Here it is assumed that the centre of gravity and the
                  inertial centre are the same.
                </p>
              </exo-help-dialog>
            </template>
            <v-row no-gutters justify="start">
              <v-col cols="3" class="mr-15">
                <exo-input-parametric
                  :root="_self"
                  name="inertialCenterInSpacecraftFrameX"
                />
              </v-col>
              <v-col cols="3" class="mr-15">
                <exo-input-parametric
                  :root="_self"
                  name="inertialCenterInSpacecraftFrameY"
                />
              </v-col>
              <v-col cols="3" class="mr-15">
                <exo-input-parametric
                  :root="_self"
                  name="inertialCenterInSpacecraftFrameZ"
                />
              </v-col>
            </v-row>
          </exo-card-form>
          <exo-card-form label="Inertial matrix at inertial centre">
            <v-row no-gutters justify="start">
              <v-col cols="3" class="mr-15">
                <exo-input-parametric :root="_self" name="inertiaXX" />
              </v-col>
              <v-col cols="3" class="mr-15">
                <exo-input-parametric :root="_self" name="inertiaXY" />
              </v-col>
              <v-col cols="3" class="mr-15">
                <exo-input-parametric :root="_self" name="inertiaXZ" />
              </v-col>
            </v-row>
            <v-row no-gutters justify="start">
              <v-col cols="3" class="mr-15">
                <exo-input-parametric :root="_self" name="inertiaYX" />
              </v-col>
              <v-col cols="3" class="mr-15">
                <exo-input-parametric :root="_self" name="inertiaYY" />
              </v-col>
              <v-col cols="3" class="mr-15">
                <exo-input-parametric :root="_self" name="inertiaYZ" />
              </v-col>
            </v-row>
            <v-row no-gutters justify="start">
              <v-col cols="3" class="mr-15">
                <exo-input-parametric :root="_self" name="inertiaZX" />
              </v-col>
              <v-col cols="3" class="mr-15">
                <exo-input-parametric :root="_self" name="inertiaZY" />
              </v-col>
              <v-col cols="3" class="mr-15">
                <exo-input-parametric :root="_self" name="inertiaZZ" />
              </v-col>
            </v-row>
            <exo-input-parametric :root="_self" name="minAngularRate" />
            <exo-input-parametric :root="_self" name="maxAngularRate" />
          </exo-card-form>
          <exo-card-form label="Aerodynamic centre in spacecraft frame">
            <template v-slot:titleTooltip>
              <exo-help-dialog
                margin="ml-5"
                title="Aerodynamic centre in spacecraft frame"
              >
                <p>
                  Distance from the origin of spacecraft frame to the point of
                  application of the aerodynamic force, also called centre of
                  pressure.
                </p>
              </exo-help-dialog>
            </template>
            <v-row no-gutters justify="start">
              <v-col cols="3" class="mr-15">
                <exo-input-parametric
                  :root="_self"
                  name="aerodynamicCenterInSpacecraftFrameX"
                />
              </v-col>
              <v-col cols="3" class="mr-15">
                <exo-input-parametric
                  :root="_self"
                  name="aerodynamicCenterInSpacecraftFrameY"
                />
              </v-col>
              <v-col cols="3" class="mr-15">
                <exo-input-parametric
                  :root="_self"
                  name="aerodynamicCenterInSpacecraftFrameZ"
                />
              </v-col>
            </v-row>
          </exo-card-form>
          <exo-input-checkbox
            :root="_self"
            gt_ParamOrbitTransfert1-v-step="7"
            name="srpModelDefined"
          />
          <exo-input-parametric
            v-if="inputs.srpModelDefined"
            :root="_self"
            name="absorptionCoefficient"
            tooltip="Used to consider the solar radiation pressure"
          >
            <exo-help-dialog title="Absorption coefficient">
              Coefficient between 0 and 1, the default value is 0.6.<br />
              Used for solar radiation pressure.<br />
            </exo-help-dialog>
          </exo-input-parametric>
          <exo-input-parametric
            v-if="inputs.srpModelDefined"
            :root="_self"
            name="reflectionCoefficient"
            tooltip="Used to consider the Solar radiation pressure"
          >
            <exo-help-dialog title="Specular reflection coefficient">
              Coefficient between 0 and 1, the default value is 0.6.<br />
              Used for solar radiation pressure.<br />
            </exo-help-dialog>
          </exo-input-parametric>
          <exo-card-form
            v-if="inputs.srpModelDefined"
            label="Solar radiation pressure centre in spacecraft frame"
          >
            <template v-slot:titleTooltip>
              <exo-help-dialog
                margin="ml-5"
                title="Solar radiation pressure centre in spacecraft frame"
              >
                <p>
                  Distance from the origin of spacecraft frame to the point of
                  application of the solar radiation pressure force.
                </p>
              </exo-help-dialog>
            </template>
            <v-row no-gutters justify="start">
              <v-col cols="3" class="mr-15">
                <exo-input-parametric
                  :root="_self"
                  name="solarRadiationPressureCenterInSpacecraftFrameX"
                />
              </v-col>
              <v-col cols="3" class="mr-15">
                <exo-input-parametric
                  :root="_self"
                  name="solarRadiationPressureCenterInSpacecraftFrameY"
                />
              </v-col>
              <v-col cols="3" class="mr-15">
                <exo-input-parametric
                  :root="_self"
                  name="solarRadiationPressureCenterInSpacecraftFrameZ"
                />
              </v-col>
            </v-row>
          </exo-card-form>
          <exo-input-ref
            :root="_self"
            name="residualMagneticMoment"
            type="analyticalMagneticTorquer"
            componentRoute="/analyticalmagnetictorquer"
          >
            <exo-help-dialog title="Residual magnetic moment">
              <p>
                The residual magnetic moment is the magnetic moment of the
                spacecraft, excluding the magnetic torquers. It creates a
                perturbative torque which depends on the geomagnetic field
                intensity and direction.
              </p>
            </exo-help-dialog>
          </exo-input-ref>
          <exo-input-ref-multiple
            :root="_self"
            name="magneticTorquers"
            :isParametric="true"
            type="analyticalMagneticTorquer"
            componentRoute="/analyticalmagnetictorquer"
          />
          <exo-form-alert
            type="warning"
            v-if="
              inputs.magneticTorquers != null &&
              inputs.magneticTorquers.length === 0
            "
          >
            The attitude dynamics model in the spacecraft requires at least one
            magnetic torquer.
          </exo-form-alert>
          <exo-input-ref-multiple
            :root="_self"
            name="reactionWheels"
            :isParametric="true"
            type="reactionWheel"
            componentRoute="/reactionwheeldefinition"
          />
          <exo-form-alert
            type="warning"
            v-if="
              inputs.reactionWheels != null &&
              inputs.reactionWheels.length === 0
            "
          >
            The attitude dynamics model in the spacecraft requires at least one
            reaction wheel.
          </exo-form-alert>
          <exo-input-ref-multiple
            :root="_self"
            name="sensors"
            :isParametric="true"
            type="analyticalSensor"
            componentRoute="/analyticalsensor"
          />
          <exo-form-alert
            type="warning"
            v-if="inputs.sensors != null && inputs.sensors.length === 0"
          >
            The attitude dynamics model in the spacecraft requires at least one
            sensor.
          </exo-form-alert>
        </div>
        <exo-input-parametric
          :onChangeCallback="dutyCycleUpdate"
          :root="_self"
          gt_ParamOrbitTransfert1-v-step="8"
          gt_ParamOrbitTransfert2-v-step="5"
          name="orbitalAveragePower"
        >
          <div gt_ParamOrbitTransfert2-v-step="6"></div>
          <exo-help-dialog title="Orbital average power for propulsion">
            The orbital average power for propulsion represents how much power a
            spacecraft can allocate to its propulsion system in average over one
            orbital period.
            <br />
            The orbital average power depends on the solar arrays, the attitude
            mode and the orbit. It can be estimated thanks to the
            <i>Maneuvering Strategy Design</i> mission in the
            <i>Simulations</i> module.
            <br />
            <br />
            For instance, if a propulsion system has a nominal power of 100 W
            and the spacecraft can only deliver 50 W per orbit on average, the
            maximum mean duty cycle achievable for any mission longer than a few
            orbital periods will be 50 %. This means that the propulsion system
            will be thrusting at most 50 % of the time on average over the
            manoeuvre.
          </exo-help-dialog>
        </exo-input-parametric>
        <exo-input-ref
          :root="_self"
          :onChangeCallback="dutyCycleUpdate"
          name="thruster"
          type="thruster"
          gt_ParamOrbitTransfert1-v-step="9"
          componentRoute="/analyticalthruster"
        />
        <exo-input-checkbox
          v-if="inputs.orbitalDutyCycle !== 0"
          alignWithParametricField
          :root="_self"
          name="customOrbitalDutyCycle"
          :onChange="dutyCycleUpdate"
        >
          <exo-help-dialog margin="mb-6 ml-2" title="Custom duty cycle">
            <tooltipDutyCycleAnalytical />
          </exo-help-dialog>
        </exo-input-checkbox>
        <exo-input-slider
          :root="_self"
          alignWithParametricField
          v-if="inputs.customOrbitalDutyCycle"
          :max="100"
          name="orbitalDutyCycle"
        />
        <exo-form-alert
          type="grey"
          v-if="inputs.customOrbitalDutyCycle && getMaxDutyCycle !== ''"
        >
          The maximum orbital duty cycle without rest orbit is
          {{ getMaxDutyCycle }} %
          <exo-form-alert v-if="Math.round(getMaxDutyCycle * 10) / 10 === 0">
            The maximum expected orbital duty cycle is less than 1 %, please
            modify your thruster so the duty cycle is at least 1 %.
          </exo-form-alert>
        </exo-form-alert>
        <exo-form-alert
          type="warning"
          v-if="inputs.customOrbitalDutyCycle && inputs.orbitalDutyCycle > 60"
        >
          A high orbital duty cycle might be inefficient when changing the
          inclination of an orbit. Please have a look at the help item.
        </exo-form-alert>
        <exo-form-alert
          type="grey"
          v-if="!inputs.customOrbitalDutyCycle && getMaxDutyCycle !== ''"
        >
          Expected orbital duty cycle :
          {{ Math.round(getMaxDutyCycle * 10) / 10 }} %
          <exo-form-alert v-if="Math.round(getMaxDutyCycle * 10) / 10 === 0">
            The maximum expected orbital duty cycle is less than 1 %, please
            modify your thruster so the duty cycle is at least 1 %.
          </exo-form-alert>
        </exo-form-alert>
        <exo-form-alert
          type="warning"
          v-if="!inputs.customOrbitalDutyCycle && getMaxDutyCycle > 60"
        >
          A high orbital duty cycle might be inefficient when changing the
          inclination of an orbit. Please have a look at the help item.
        </exo-form-alert>
        <div gt_ParamOrbitTransfert2-v-step="7"></div>
        <div
          v-if="this.gt_OrbitTransferParam1.thrusterTourDone"
          gt_ParamOrbitTransfert1-v-step="18"
        ></div>
      </exo-form>
    </exo-table>
    <exo-modal-references :root="_self" :modalContent="modalRef" />
    <v-tour
      name="paramOrbitalTransfer1TourAnaSC"
      :steps="steps_analytical_spacecraft"
      :callbacks="customCallbacks"
    ></v-tour>
    <v-tour
      name="paramOrbitalTransfer2TourAnaSC"
      :steps="steps_analytical_spacecraft2"
      :callbacks="customCallbacks"
    ></v-tour>
  </div>
</template>

<script>
import formMixin from '../../mixins/forms.js'
import analyticalMixin from '../../mixins/analyticalFoms.js'
import dataTools from '../../tools/data.js'
import tooltipDutyCycleAnalytical from '../../components/Tooltips/tooltipDutyCycleAnalytical'
import { mapGetters } from 'vuex'
import guidedTutorialParamOrbitalTransfer1 from '../../components/GuidedTutorials/guidedTutorialParamOrbitalTransfer1.js'
import { steps_analytical_spacecraft } from '../../components/GuidedTutorials/guidedTutorialParamOrbitalTransfer1.js'
import { steps_analytical_spacecraft2 } from '../../components/GuidedTutorials/guidedTutorialParamOrbitalTransfer2.js'
import guidedTutorialParamOrbitalTransfer2 from '../../components/GuidedTutorials/guidedTutorialParamOrbitalTransfer2.js'

export default {
  name: 'analyticalspacecraft',

  components: {
    tooltipDutyCycleAnalytical
  },

  mixins: [formMixin, analyticalMixin],

  mounted: function () {
    this.refreshReferences()
    if (this.gt_OrbitTransferParam1.isActive) {
      guidedTutorialParamOrbitalTransfer1.beginTourAnaSC(this)
    }
    if (this.gt_OrbitTransferParam2.isActive) {
      this.$tours['paramOrbitalTransfer2TourAnaSC'].start()
    }
  },

  computed: {
    ...mapGetters(['gt_OrbitTransferParam1', 'gt_OrbitTransferParam2']),
    getMaxDutyCycle() {
      if (
        !this.inputs.parametricValues.orbitalAveragePower.active &&
        !this.inputs.parametricReferences.thruster.active &&
        this.inputs.thruster != null &&
        !isNaN(this.inputs.thruster.power) &&
        !isNaN(this.inputs.orbitalAveragePower)
      ) {
        return dataTools.computeMaxDutyCycle(
          this.inputs.orbitalAveragePower,
          this.inputs.thruster.power,
          this.inputs.thruster.standbyPower
        )
      } else {
        return ''
      }
    }
  },

  data() {
    return {
      tableFields: [
        { value: 'displayedName', text: 'Name', sortable: true },
        {
          value: 'platformMass',
          text: 'Platform mass' + this.getDefaultUnitLabel('platformMass'),
          sortable: true
        },
        {
          value: 'dragArea',
          text: 'Drag area' + this.getDefaultUnitLabel('dragArea'),
          sortable: true
        },
        {
          value: 'orbitalAveragePower',
          text:
            'Orbital average power for propulsion' +
            this.getDefaultUnitLabel('orbitalAveragePower'),
          sortable: true
        },
        { value: 'thruster', text: 'Propulsion system', sortable: true },
        { value: 'actions', text: 'Actions', sortable: false }
      ],
      steps_analytical_spacecraft: steps_analytical_spacecraft,
      steps_analytical_spacecraft2: steps_analytical_spacecraft2,
      customCallbacks: {
        onNextStep: (currentStep) => {
          if (this.gt_OrbitTransferParam1.isActive) {
            guidedTutorialParamOrbitalTransfer1.onNextStepAnaSC(
              currentStep,
              this
            )
          } else {
            guidedTutorialParamOrbitalTransfer2.onNextAnaSC(this, currentStep)
          }
        },
        onSkip: () => {
          if (this.gt_OrbitTransferParam1.isActive) {
            guidedTutorialParamOrbitalTransfer1.onSkipAnaSC(this)
          } else {
            guidedTutorialParamOrbitalTransfer2.onSkipAnaSC(this)
          }
        },
        onFinish: () => {
          this.submitForm()
        }
      },
      thrusters: [],
      thrustersNotParametricsId: [],
      analyticalMagneticTorquers: [],
      analyticalMagneticTorquersNotParametricsId: [],
      reactionWheels: [],
      reactionWheelsNotParametrics: [],
      analyticalSensors: [],
      analyticalSensorsNotParametrics: [],

      modalRef: { content: [] },
      showModal: false,

      inputs: {
        platformMass: this.getDefaultValue('platformMass'),
        // Drag model
        dragModelDefined: this.getDefaultValue('dragModelDefined'),
        dragArea: this.getDefaultValue('dragArea'),
        dragCoefficient: this.getDefaultValue('dragCoefficient'),
        // Srp model
        srpModelDefined: this.getDefaultValue('srpModelDefined'),
        absorptionCoefficient: this.getDefaultValue('absorptionCoefficient'),
        reflectionCoefficient: this.getDefaultValue('reflectionCoefficient'),
        // Attitude model
        attitudeDynamicsModelDefined: this.getDefaultValue(
          'attitudeDynamicsModelDefined'
        ),
        inertialCenterInSpacecraftFrameX: this.getDefaultValue(
          'inertialCenterInSpacecraftFrameX'
        ),
        inertialCenterInSpacecraftFrameY: this.getDefaultValue(
          'inertialCenterInSpacecraftFrameY'
        ),
        inertialCenterInSpacecraftFrameZ: this.getDefaultValue(
          'inertialCenterInSpacecraftFrameZ'
        ),
        aerodynamicCenterInSpacecraftFrameX: this.getDefaultValue(
          'aerodynamicCenterInSpacecraftFrameX'
        ),
        aerodynamicCenterInSpacecraftFrameY: this.getDefaultValue(
          'aerodynamicCenterInSpacecraftFrameY'
        ),
        aerodynamicCenterInSpacecraftFrameZ: this.getDefaultValue(
          'aerodynamicCenterInSpacecraftFrameZ'
        ),
        solarRadiationPressureCenterInSpacecraftFrameX: this.getDefaultValue(
          'solarRadiationPressureCenterInSpacecraftFrameX'
        ),
        solarRadiationPressureCenterInSpacecraftFrameY: this.getDefaultValue(
          'solarRadiationPressureCenterInSpacecraftFrameY'
        ),
        solarRadiationPressureCenterInSpacecraftFrameZ: this.getDefaultValue(
          'solarRadiationPressureCenterInSpacecraftFrameZ'
        ),
        inertiaXX: this.getDefaultValue('inertiaXX'),
        inertiaXY: this.getDefaultValue('inertiaXY'),
        inertiaXZ: this.getDefaultValue('inertiaXZ'),
        inertiaYX: this.getDefaultValue('inertiaYX'),
        inertiaYY: this.getDefaultValue('inertiaYY'),
        inertiaYZ: this.getDefaultValue('inertiaYZ'),
        inertiaZX: this.getDefaultValue('inertiaZX'),
        inertiaZY: this.getDefaultValue('inertiaZY'),
        inertiaZZ: this.getDefaultValue('inertiaZZ'),
        minAngularRate: this.getDefaultValue('minAngularRate'),
        maxAngularRate: this.getDefaultValue('maxAngularRate'),
        residualMagneticMoment: this.getDefaultValue('residualMagneticMoment'),
        magneticTorquers: null,
        reactionWheels: null,
        sensors: null,
        // Power model
        orbitalAveragePower: this.getDefaultValue('orbitalAveragePower'),
        orbitalDutyCycle: this.getDefaultValue('orbitalDutyCycle'),
        customOrbitalDutyCycle: this.getDefaultValue('customOrbitalDutyCycle'),
        // Propulsion system
        thruster: null,
        parametricValues: {
          platformMass: this.buildDefaultParametricValue('platformMass'),
          dragArea: this.buildDefaultParametricValue('dragArea'),
          dragCoefficient: this.buildDefaultParametricValue('dragCoefficient'),
          absorptionCoefficient: this.buildDefaultParametricValue(
            'absorptionCoefficient'
          ),
          reflectionCoefficient: this.buildDefaultParametricValue(
            'reflectionCoefficient'
          ),
          orbitalAveragePower: this.buildDefaultParametricValue(
            'orbitalAveragePower'
          ),
          inertialCenterInSpacecraftFrameX: this.buildDefaultParametricValue(
            'inertialCenterInSpacecraftFrameX'
          ),
          inertialCenterInSpacecraftFrameY: this.buildDefaultParametricValue(
            'inertialCenterInSpacecraftFrameY'
          ),
          inertialCenterInSpacecraftFrameZ: this.buildDefaultParametricValue(
            'inertialCenterInSpacecraftFrameZ'
          ),
          aerodynamicCenterInSpacecraftFrameX: this.buildDefaultParametricValue(
            'aerodynamicCenterInSpacecraftFrameX'
          ),
          aerodynamicCenterInSpacecraftFrameY: this.buildDefaultParametricValue(
            'aerodynamicCenterInSpacecraftFrameY'
          ),
          aerodynamicCenterInSpacecraftFrameZ: this.buildDefaultParametricValue(
            'aerodynamicCenterInSpacecraftFrameZ'
          ),
          inertiaXX: this.buildDefaultParametricValue('inertiaXX'),
          inertiaXY: this.buildDefaultParametricValue('inertiaXY'),
          inertiaXZ: this.buildDefaultParametricValue('inertiaXZ'),
          inertiaYX: this.buildDefaultParametricValue('inertiaYX'),
          inertiaYY: this.buildDefaultParametricValue('inertiaYY'),
          inertiaYZ: this.buildDefaultParametricValue('inertiaYZ'),
          inertiaZX: this.buildDefaultParametricValue('inertiaZX'),
          inertiaZY: this.buildDefaultParametricValue('inertiaZY'),
          inertiaZZ: this.buildDefaultParametricValue('inertiaZZ'),
          minAngularRate: this.buildDefaultParametricValue('minAngularRate'),
          maxAngularRate: this.buildDefaultParametricValue('maxAngularRate'),
          solarRadiationPressureCenterInSpacecraftFrameX:
            this.buildDefaultParametricValue(
              'solarRadiationPressureCenterInSpacecraftFrameX'
            ),
          solarRadiationPressureCenterInSpacecraftFrameY:
            this.buildDefaultParametricValue(
              'solarRadiationPressureCenterInSpacecraftFrameY'
            ),
          solarRadiationPressureCenterInSpacecraftFrameZ:
            this.buildDefaultParametricValue(
              'solarRadiationPressureCenterInSpacecraftFrameZ'
            )
        },
        parametricReferences: {
          thruster: this.buildDefaultParametricReference('thruster'),
          residualMagneticMoment: this.buildDefaultParametricReference(
            'residualMagneticMoment'
          )
        }
      }
    }
  },

  methods: {
    refreshReferences() {
      this.refreshThrusters()
      this.refreshMagneticTorquers()
      this.refreshReactionWheels()
      this.refreshSensors()
    },

    getRequiredPropertiesList(instance) {
      if (this.getCurrentFormDescription().required == null) {
        return []
      }
      let requiredList = this.getCurrentFormDescription().required.slice()
      if (this.inputs.dragModelDefined) {
        requiredList.push('dragCoefficient')
        requiredList.push('dragArea')
      }
      if (this.inputs.srpModelDefined) {
        requiredList.push('absorptionCoefficient')
        requiredList.push('reflectionCoefficient')
      }
      if (this.inputs.attitudeDynamicsModelDefined) {
        requiredList.push('inertialCenterInSpacecraftFrameX')
        requiredList.push('inertialCenterInSpacecraftFrameY')
        requiredList.push('inertialCenterInSpacecraftFrameZ')
        requiredList.push('aerodynamicCenterInSpacecraftFrameX')
        requiredList.push('aerodynamicCenterInSpacecraftFrameY')
        requiredList.push('aerodynamicCenterInSpacecraftFrameZ')
        requiredList.push('solarRadiationPressureCenterInSpacecraftFrameX')
        requiredList.push('solarRadiationPressureCenterInSpacecraftFrameY')
        requiredList.push('solarRadiationPressureCenterInSpacecraftFrameZ')
        requiredList.push('inertiaXX')
        requiredList.push('inertiaXY')
        requiredList.push('inertiaXZ')
        requiredList.push('inertiaYX')
        requiredList.push('inertiaYY')
        requiredList.push('inertiaYZ')
        requiredList.push('inertiaZX')
        requiredList.push('inertiaZY')
        requiredList.push('inertiaZZ')
        requiredList.push('maxAngularRate')
        requiredList.push('minAngularRate')
        requiredList.push('residualMagneticMoment')
        requiredList.push('magneticTorquers')
        requiredList.push('reactionWheels')
        requiredList.push('sensors')
      }
      return requiredList
    },

    refreshThrusters() {
      // TODO MFI automatic method for all of these getters
      let self = this
      this.$http
        .get(this.getAPI(`parametricpropulsionsystemdefinitions`))
        .then((response) => {
          self.thrusters = response.data
            .map((obj) => ({ value: obj, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
          self.thrustersNotParametricsId = response.data
            .filter((obj) => !obj.displayedName.endsWith('*'))
            .map((obj) => ({ value: obj.id, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
        })
        .catch((e) => {
          self.displayMessage(
            'Error getting list of thrusters. ',
            e,
            'notification',
            'error'
          )
        })
    },

    refreshMagneticTorquers() {
      let self = this
      this.$http
        .get(this.getAPI(`parametricmagnetictorquerdefinitions`))
        .then((response) => {
          self.analyticalMagneticTorquers = response.data
            .map((obj) => ({ value: obj, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
          self.analyticalMagneticTorquersNotParametricsId = response.data
            .filter((obj) => !obj.displayedName.endsWith('*'))
            .map((obj) => ({ value: obj.id, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
        })
        .catch((e) => {
          self.displayMessage(
            'Error getting list of magnetic torquers. ',
            e,
            'notification',
            'error'
          )
        })
    },

    refreshReactionWheels() {
      let self = this
      this.$http
        .get(this.getAPI(`reactionwheeldefinitions`))
        .then((response) => {
          self.reactionWheels = response.data
            .map((obj) => ({ value: obj, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
          self.reactionWheelsNotParametrics = response.data
            .filter((obj) => !obj.displayedName.endsWith('*'))
            .map((obj) => ({ value: obj.id, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
        })
        .catch((e) => {
          self.displayMessage(
            'Error getting list of reaction wheels. ',
            e,
            'notification',
            'error'
          )
        })
    },

    refreshSensors() {
      let self = this
      this.$http
        .get(this.getAPI(`parametricsensordefinitions`))
        .then((response) => {
          self.analyticalSensors = response.data
            .map((obj) => ({ value: obj, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
          self.analyticalSensorsNotParametrics = response.data
            .filter((obj) => !obj.displayedName.endsWith('*'))
            .map((obj) => ({ value: obj.id, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
        })
        .catch((e) => {
          self.displayMessage(
            'Error getting list of sensors. ',
            e,
            'notification',
            'error'
          )
        })
    },

    dutyCycleUpdate() {
      this.inputs.orbitalDutyCycle = 100
      if (this.getMaxDutyCycle !== '') {
        this.inputs.orbitalDutyCycle = this.getMaxDutyCycle
      }
    },

    getCurrentFormType() {
      return 'ParametricSpacecraftDefinition'
    },

    getRefList(param) {
      if (param === 'thruster') {
        return this.thrusters
      }
    },

    updateDragModelDefined() {
      if (this.inputs.attitudeDynamicsModelDefined === true) {
        this.inputs.dragModelDefined = true
      }
    },
    isExoInputMultiple(item) {
      return (
        item === 'magneticTorquers' ||
        item === 'reactionWheels' ||
        item === 'sensors'
      )
    }
  }
}
</script>
